import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Users, Calendar, Send, Phone, Mail, PartyPopper, Briefcase, Cake, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventsPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    event_date: "",
    guests: "",
    event_type: "",
    message: "",
  });

  const eventTypes = [
    { value: "corporate", label: t("events.type_corporate"), icon: Briefcase },
    { value: "birthday", label: t("events.type_birthday"), icon: Cake },
    { value: "family", label: t("events.type_family"), icon: Heart },
    { value: "other", label: t("events.type_other"), icon: PartyPopper },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/events`, {
        ...formData,
        guests: parseInt(formData.guests) || 0,
      });
      toast.success("Votre demande a été envoyée avec succès ! Nous vous contacterons rapidement.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        event_date: "",
        guests: "",
        event_type: "",
        message: "",
      });
    } catch (error) {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="pt-20" data-testid="events-page">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src="/images/plats/gouter.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-gold font-accent text-lg tracking-widest">
              {t("events.subtitle")}
            </span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl mt-2 mb-6">
              {t("events.title")}
            </h1>
            <div className="section-divider mx-auto bg-gradient-to-r from-transparent via-gold to-transparent" />
          </motion.div>
        </div>
      </section>

      {/* Description */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-body max-w-3xl mx-auto">
              {t("events.description")}
            </p>
          </motion.div>

          {/* Event Types Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {eventTypes.map((type, i) => (
              <motion.div
                key={type.value}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center p-6 bg-secondary rounded card-elegant"
              >
                <div className="w-14 h-14 mx-auto mb-4 bg-gold/10 rounded-full flex items-center justify-center">
                  <type.icon className="h-7 w-7 text-gold" />
                </div>
                <h3 className="font-heading text-lg text-primary">{type.label}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 md:py-24 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl text-primary mb-4">
                Demande de réservation
              </h2>
              <p className="text-muted-foreground font-body">
                Remplissez le formulaire ci-dessous et nous vous contacterons rapidement.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-card p-8 md:p-12 shadow-lg border border-border/40">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="font-body">{t("events.form_name")}</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    required
                    className="rounded-none border-border"
                    data-testid="event-name-input"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="font-body">{t("events.form_email")}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    required
                    className="rounded-none border-border"
                    data-testid="event-email-input"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="font-body">{t("events.form_phone")}</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    required
                    className="rounded-none border-border"
                    data-testid="event-phone-input"
                  />
                </div>

                {/* Date */}
                <div className="space-y-2">
                  <Label htmlFor="event_date" className="font-body">{t("events.form_date")}</Label>
                  <Input
                    id="event_date"
                    type="date"
                    value={formData.event_date}
                    onChange={(e) => handleChange("event_date", e.target.value)}
                    required
                    className="rounded-none border-border"
                    data-testid="event-date-input"
                  />
                </div>

                {/* Guests */}
                <div className="space-y-2">
                  <Label htmlFor="guests" className="font-body">{t("events.form_guests")}</Label>
                  <Input
                    id="guests"
                    type="number"
                    min="1"
                    value={formData.guests}
                    onChange={(e) => handleChange("guests", e.target.value)}
                    required
                    className="rounded-none border-border"
                    data-testid="event-guests-input"
                  />
                </div>

                {/* Event Type */}
                <div className="space-y-2">
                  <Label htmlFor="event_type" className="font-body">{t("events.form_type")}</Label>
                  <Select
                    value={formData.event_type}
                    onValueChange={(value) => handleChange("event_type", value)}
                    required
                  >
                    <SelectTrigger className="rounded-none border-border" data-testid="event-type-select">
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {eventTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Message */}
              <div className="space-y-2 mt-6">
                <Label htmlFor="message" className="font-body">{t("events.form_message")}</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  rows={4}
                  className="rounded-none border-border resize-none"
                  placeholder="Décrivez votre événement, vos besoins particuliers..."
                  data-testid="event-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full mt-8 bg-gold hover:bg-gold/90 text-white rounded-none py-6 text-sm tracking-wider uppercase btn-hover-expand"
                data-testid="event-submit-btn"
              >
                {loading ? (
                  "Envoi en cours..."
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    {t("events.form_submit")}
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Direct Contact */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-heading text-2xl md:text-3xl text-primary mb-6">
              Ou contactez-nous directement
            </h2>

            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a
                href="tel:+41223424243"
                className="flex items-center justify-center gap-3 p-4 bg-secondary hover:bg-secondary/80 transition-colors rounded"
              >
                <Phone className="h-5 w-5 text-gold" />
                <span className="font-heading text-lg text-primary">{t("common.phone_display")}</span>
              </a>

              <a
                href="mailto:reservation@brasseriecarougeoise.ch"
                className="flex items-center justify-center gap-3 p-4 bg-secondary hover:bg-secondary/80 transition-colors rounded"
              >
                <Mail className="h-5 w-5 text-gold" />
                <span className="font-body text-primary">reservation@brasseriecarougeoise.ch</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
